import { useMemo, useState } from "react";
import { colors } from "../../style";
import { speakerColours } from "../../utils/meetingFormat";
import { Card, Icon, LinkButton, ui } from "../ui";
import { RenameSpeakerDialog, renameTarget } from "./MeetingTranscript";

// Share of words spoken, not time: call transcripts have no timings.
// With rename, each name opens the transcript's rename dialog.
export function TalkShareCard({
  meeting,
  rename,
  subject = "meeting",
  onShowTranscript,
}) {
  const [target, setTarget] = useState(null);
  const conversation = useMemo(
    () => meeting.transcription?.conversation ?? [],
    [meeting.transcription],
  );
  const rows = useMemo(() => {
    const colours = speakerColours(conversation);
    const words = new Map();
    for (const b of conversation) {
      const n = (b.content ?? "").split(/\s+/).filter(Boolean).length;
      words.set(b.participant, (words.get(b.participant) ?? 0) + n);
    }
    const total = [...words.values()].reduce((a, b) => a + b, 0);
    if (words.size < 2 || total === 0) return [];
    return [...words]
      .map(([name, n]) => ({
        name,
        colour: colours.get(name) ?? colors.teal,
        share: n / total,
      }))
      .sort((a, b) => b.share - a.share);
  }, [conversation]);

  if (!rows.length) return null;
  return (
    <Card title="Who spoke most">
      {rows.map((r) => (
        <div
          key={r.name}
          style={{
            display: "grid",
            gridTemplateColumns: "7px minmax(0, 9em) 1fr 3em",
            alignItems: "center",
            gap: "10px",
            fontSize: "13px",
          }}
        >
          <span
            style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: r.colour,
            }}
          />
          {rename ? (
            <button
              type="button"
              title="Rename this speaker"
              onClick={() => setTarget(renameTarget(conversation, r.name))}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                minWidth: 0,
                padding: 0,
                border: 0,
                background: "none",
                color: "inherit",
                font: "inherit",
                textAlign: "left",
                cursor: "pointer",
              }}
            >
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {r.name || "Speaker"}
              </span>
              <span style={{ ...ui.muted, flexShrink: 0, display: "flex" }}>
                <Icon name="pencil" size={11} />
              </span>
            </button>
          ) : (
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {r.name || "Speaker"}
            </span>
          )}
          <span
            style={{
              height: 4,
              borderRadius: 2,
              background: colors.border,
              overflow: "hidden",
            }}
          >
            <span
              style={{
                display: "block",
                height: "100%",
                width: `${r.share * 100}%`,
                background: r.colour,
              }}
            />
          </span>
          <span
            style={{
              ...ui.muted,
              textAlign: "right",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {Math.round(r.share * 100)}%
          </span>
        </div>
      ))}
      {onShowTranscript && (
        <LinkButton
          onClick={onShowTranscript}
          style={{ alignSelf: "flex-start", marginTop: "4px" }}
        >
          Wrong names? Fix them in the transcript
        </LinkButton>
      )}
      {rename && (
        <RenameSpeakerDialog
          meeting={meeting}
          rename={rename}
          subject={subject}
          target={target}
          onClose={() => setTarget(null)}
        />
      )}
    </Card>
  );
}
